import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../../lib/supabase';
import { getAllCourseReviews, toggleReviewPublicationStatus, deleteCourseReview } from '../../../services/elearningReviewService';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { 
  type ElearningCourse, 
  type ElearningCategory 
} from '../../../services/elearningCourseService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../hooks/useToast';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { 
  BookOpen, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Users, 
  Clock, 
  Star,
  MoreHorizontal,
  Copy,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  X,
  DollarSign,
  LayoutGrid
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

// Type pour les avis de cours
type CourseReview = {
  id: string;
  course_id: string;
  student_id: string;
  rating: number;
  review_text: string;
  is_published: boolean;
  created_at: string;
  student: {
    id: string;
    name: string;
    avatar_url: string;
  };
};

const AdminCoursesPage: React.FC = () => {
  useCheckPermissions(['admin']);
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const [courses, setCourses] = useState<ElearningCourse[]>([]);
  const [categories, setCategories] = useState<ElearningCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'title' | 'enrollment_count'>('created_at');
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    cancelLabel: string;
    onConfirm: () => void;
  }>({ 
    title: '', 
    message: '', 
    confirmLabel: '', 
    cancelLabel: '', 
    onConfirm: () => {} 
  });
  
  // État pour la modale des avis
  const [isReviewsDialogOpen, setIsReviewsDialogOpen] = useState(false);
  const [currentCourseReviews, setCurrentCourseReviews] = useState<CourseReview[]>([]);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'published' | 'pending'>('all');
  const [currentCourseTitle, setCurrentCourseTitle] = useState<string>('');
  const [currentCourseId, setCurrentCourseId] = useState<string>('');
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [totalInstructors, setTotalInstructors] = useState(0);
  const [averageDuration, setAverageDuration] = useState(0);
  const [averagePrice, setAveragePrice] = useState(0);
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(10);

  useEffect(() => {
    loadData();
    fetchTotalInstructors();
  }, [tenant?.id, selectedCategory, selectedStatus, sortBy]);

  const fetchTotalInstructors = async () => {
    try {
      let query = supabase
        .from('elearn_instructors')
        .select('*', { count: 'exact', head: true });
      
      // Appliquer le filtre domaine
      query = applyDomainFilterToQuery(query);
      
      const { count, error } = await query;
      if (error) throw error;
      setTotalInstructors(count || 0);
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre d\'instructeurs:', error);
    }
  };
  
  // Fonction pour charger les avis d'un cours
  const loadCourseReviews = async (courseId: string, courseTitle: string) => {
    if (!tenant?.id) return;
    
    setIsLoadingReviews(true);
    setCurrentCourseTitle(courseTitle);
    setCurrentCourseId(courseId);
    
    try {
      const reviews = await getAllCourseReviews(tenant.id, courseId);
      setCurrentCourseReviews(reviews);
      setIsReviewsDialogOpen(true);
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les avis du cours.',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const handleToggleReviewStatus = async (reviewId: string, currentStatus: boolean) => {
    if (!tenant?.id || !currentCourseId) return;
    
    try {
      const success = await toggleReviewPublicationStatus(tenant.id, reviewId, !currentStatus);
      
      if (success) {
        // Mettre à jour l'état local
        setCurrentCourseReviews(prevReviews => 
          prevReviews.map(review => 
            review.id === reviewId 
              ? { ...review, is_published: !currentStatus } 
              : review
          )
        );
        
        toast({
          title: !currentStatus ? 'Avis publié' : 'Avis dépublié',
          description: !currentStatus 
            ? 'L\'avis est maintenant visible pour les étudiants.' 
            : 'L\'avis n\'est plus visible pour les étudiants.',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la modification du statut de l\'avis:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le statut de l\'avis.',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!tenant?.id || !currentCourseId) return;
    
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet avis ? Cette action est irréversible.')) {
      return;
    }
    
    try {
      const success = await deleteCourseReview(tenant.id, reviewId);
      
      if (success) {
        // Mettre à jour l'état local
        setCurrentCourseReviews(prevReviews => 
          prevReviews.filter(review => review.id !== reviewId)
        );
        
        toast({
          title: 'Avis supprimé',
          description: 'L\'avis a été supprimé avec succès.',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'avis:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer l\'avis.',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    loadData();
  }, [tenant?.id, selectedCategory, selectedStatus, sortBy]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Charger les catégories avec filtrage domaine
      let categoriesQuery = supabase
        .from('elearn_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      // Appliquer le filtre domaine
      categoriesQuery = applyDomainFilterToQuery(categoriesQuery);
      
      const { data: categoriesData, error: categoriesError } = await categoriesQuery;

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData);

      // Construire la requête pour les cours
      let coursesQuery = supabase
        .from('elearn_courses')
        .select(`
          *,
          category:category_id(id, name, slug, color),
          instructor:instructor_id(id, name, avatar_url, name:name)
        `);
      
      // Appliquer le filtre domaine
      coursesQuery = applyDomainFilterToQuery(coursesQuery);

      // Appliquer le tri
      if (sortBy === 'instructor_name') {
        coursesQuery = coursesQuery.order('instructor.name', { ascending: true });
      } else {
        coursesQuery = coursesQuery.order(sortBy, { ascending: sortBy === 'title' });
      }

      // Filtrer par catégorie si nécessaire
      if (selectedCategory !== 'all') {
        const category = categoriesData.find(cat => cat.slug === selectedCategory);
        if (category) {
          coursesQuery = coursesQuery.eq('category_id', category.id);
        }
      }

      // Filtrer par statut si nécessaire
      if (selectedStatus !== 'all') {
        coursesQuery = coursesQuery.eq('status', selectedStatus);
      }

      // Exécuter la requête
      const { data: coursesData, error: coursesError } = await coursesQuery;

      if (coursesError) throw coursesError;
      setCourses(coursesData || []);

      // Calculer la durée moyenne
      const totalDuration = (coursesData || []).reduce((sum, course) => sum + (course.duration_hours || 0), 0);
      const avgDuration = coursesData && coursesData.length > 0 ? totalDuration / coursesData.length : 0;
      setAverageDuration(avgDuration);

      // Calculer le prix moyen
      const totalPrice = (coursesData || []).reduce((sum, course) => sum + (course.price || 0), 0);
      const avgPrice = coursesData && coursesData.length > 0 ? totalPrice / coursesData.length : 0;
      setAveragePrice(avgPrice);

    } catch (error) {
      console.error('Erreur lors du chargement des cours:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les cours',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCourse = (courseId: string) => {
    // Ouvrir une boîte de dialogue de confirmation
    setConfirmDialogProps({
      title: 'Supprimer ce cours ?',
      message: 'Cette action est irréversible. Toutes les données associées à ce cours seront supprimées.',
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
      onConfirm: async () => {
        try {
          // Supprimer le cours de la base de données
          let deleteQuery = supabase
          .from('elearn_courses')
          .delete()
          .eq('id', courseId);
        
        // Appliquer le filtre domaine
        deleteQuery = applyDomainFilterToQuery(deleteQuery);
        
        const { error } = await deleteQuery;
            
          if (error) throw error;
          
          toast({
            title: 'Cours supprimé',
            description: 'Le cours a été supprimé avec succès.'
          });
          
          // Recharger les données
          loadData();
        } catch (error) {
          console.error('Erreur lors de la suppression du cours:', error);
          toast({
            title: 'Erreur',
            description: 'Impossible de supprimer le cours',
            variant: 'destructive'
          });
        }
      }
    });
    setIsConfirmDialogOpen(true);
  };

  // Réinitialiser la page courante lorsque les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedStatus, sortBy]);

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Calculer les indices des cours à afficher sur la page courante
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredCourses.slice(indexOfFirstCourse, indexOfLastCourse);
  
  // Fonction pour changer de page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { label: 'Brouillon', variant: 'secondary' as const },
      published: { label: 'Publié', variant: 'success' as const },
      archived: { label: 'Archivé', variant: 'destructive' as const }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getDifficultyBadge = (level: string) => {
    const levelConfig = {
      beginner: { label: 'Débutant', color: 'bg-green-500/20 text-green-400' },
      intermediate: { label: 'Intermédiaire', color: 'bg-yellow-500/20 text-yellow-400' },
      advanced: { label: 'Avancé', color: 'bg-red-500/20 text-red-400' }
    };
    
    const config = levelConfig[level as keyof typeof levelConfig] || levelConfig.beginner;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (hours: number) => {
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours === 1) return '1h';
    return `${hours}h`;
  };

  // Fonction pour dupliquer un cours
  const handleDuplicateCourse = async (courseId: string) => {
    try {
      // Récupérer le cours original avec filtrage domaine
      let courseQuery = supabase
        .from('elearn_courses')
        .select('*')
        .eq('id', courseId);
      
      // Appliquer le filtre domaine
      courseQuery = applyDomainFilterToQuery(courseQuery);
      
      const { data: originalCourse, error: fetchError } = await courseQuery.single();
      
      if (fetchError) throw fetchError;
      if (!originalCourse) throw new Error('Cours introuvable');
      
      // Récupérer les paramètres de domaine
      const { domain, tenantId } = getDomainFilterParams();
      
      // Créer la copie
      const { data: original, error } = await supabase
        .from('elearn_courses')
        .select('*')
        .eq('id', courseId)
        .single();

      if (error) throw error;
      if (!original) return;

      // Créer une copie avec nouvel ID et nouveau slug
      const duplicate = { 
        ...original, 
        id: uuidv4(),
        slug: `${original.slug}-copie-${Date.now()}`,
        created_at: new Date().toISOString(),
        title: `${original.title} (copie)`,
        status: 'draft' // La copie est toujours en brouillon
      };

      // Insérer la copie dans la base de données
      const { error: insertError } = await supabase
        .from('elearn_courses')
        .insert([duplicate]);

      if (insertError) throw insertError;

      toast({
        title: 'Succès',
        description: 'Cours dupliqué avec succès',
      });

      // Rafraîchir les données
      loadData();
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de dupliquer le cours',
        variant: 'destructive',
      });
    }
  };

  // Rendu conditionnel pour la boîte de dialogue de confirmation
  const renderConfirmDialog = () => {
    if (!isConfirmDialogOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-black/90 border border-white/10 rounded-lg p-6 max-w-md w-full">
          <h3 className="text-xl font-bold text-white mb-2">{confirmDialogProps.title}</h3>
          <p className="text-white/70 mb-6">{confirmDialogProps.message}</p>
          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => setIsConfirmDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              {confirmDialogProps.cancelLabel}
            </Button>
            <Button 
              onClick={() => {
                confirmDialogProps.onConfirm();
                setIsConfirmDialogOpen(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {confirmDialogProps.confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-[#D4AF37]" />
              Gestion des Cours
            </h1>
            <p className="text-gray-400 mt-2">
              {filteredCourses.length} cours trouvé{filteredCourses.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button 
            onClick={() => navigate('/admin/elearning/create')}
            className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
          >
            <Plus className="mr-2" size={18} />
            Nouveau cours
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{courses.length}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Publiés</div>
            <div className="text-2xl font-bold text-green-400">
              {courses.filter(c => c.status === 'published').length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Instructeurs</div>
            <div className="text-2xl font-bold text-blue-400">
              {totalInstructors}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Durée moyenne</div>
            <div className="text-2xl font-bold text-purple-400">
              {Math.round(averageDuration)}h
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Inscriptions</div>
            <div className="text-2xl font-bold text-[#D4AF37]">
              {courses.reduce((sum, course) => sum + course.enrollment_count, 0)}
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Recherche */}
            <div className="relative md:col-span-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
              />
            </div>

            {/* Filtre Catégorie */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50"
              >
                <option value="all" className="bg-gray-900">Toutes catégories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.slug} className="bg-gray-900">
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre Statut */}
            <div>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50"
              >
                <option value="all" className="bg-gray-900">Tous statuts</option>
                <option value="published" className="bg-gray-900">Publié</option>
                <option value="draft" className="bg-gray-900">Brouillon</option>
                <option value="archived" className="bg-gray-900">Archivé</option>
              </select>
            </div>

            {/* Tri */}
            <div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'created_at' | 'title' | 'enrollment_count')}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50"
              >
                <option value="created_at" className="bg-gray-900">Date création</option>
                <option value="title" className="bg-gray-900">Titre</option>
                <option value="enrollment_count" className="bg-gray-900">Inscriptions</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table des cours */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucun cours trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Cours
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Catégorie
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Niveau
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Prix
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Durée
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Inscriptions
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Note
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {currentCourses.map((course, index) => (
                    <tr
                      key={course.id}
                      className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                            {course.thumbnail_url ? (
                              <img
                                src={course.thumbnail_url}
                                alt={course.title}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <BookOpen className="h-5 w-5 text-[#D4AF37]" />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {course.title}
                            </div>
                            <div className="text-sm text-gray-400">
                              {course.instructor?.name || 'Aucun instructeur'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {course.category ? (
                          <Badge 
                            style={{ backgroundColor: course.category.color + '20', color: course.category.color }}
                          >
                            {course.category.name}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">Aucune</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(course.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getDifficultyBadge(course.difficulty_level)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{formatPrice(course.price)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{formatDuration(course.duration_hours)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{course.enrollment_count}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-white">
                          <Star size={14} className="mr-1 text-yellow-400" />
                          {course.rating_average.toFixed(1)}
                          <span 
                            className="text-gray-400 ml-1 cursor-pointer hover:text-white transition-colors"
                            onClick={() => loadCourseReviews(course.id, course.title)}
                          >
                            ({course.rating_count})
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-1">
                          {course.status === 'published' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => window.open(`/elearning/courses/${course.slug}`, '_blank')}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Eye size={18} />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-[#D4AF37] hover:text-[#B8941F]"
                            onClick={() => navigate(`/admin/elearning/courses/edit/${course.id}`)}
                          >
                            <Edit size={18} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-indigo-400 hover:text-indigo-300"
                            onClick={() => navigate(`/admin/elearning/courses/builder/${course.id}`)}
                          >
                            <LayoutGrid size={18} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-green-400 hover:text-green-300"
                            onClick={() => handleDuplicateCourse(course.id)}
                          >
                            <Copy size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCourse(course.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Contrôles de pagination */}
          {filteredCourses.length > 0 && (
            <div className="flex justify-between items-center p-4 border-t border-white/10">
              <div className="text-sm text-white/70">
                Cours ({filteredCourses.length}) - Page {currentPage}/{totalPages}
              </div>
              <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="border-white/10 text-white hover:bg-white/5"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Afficher les pages autour de la page courante
                let pageToShow;
                if (totalPages <= 5) {
                  // Si moins de 5 pages, afficher toutes les pages
                  pageToShow = i + 1;
                } else if (currentPage <= 3) {
                  // Si on est au début, afficher les 5 premières pages
                  pageToShow = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // Si on est à la fin, afficher les 5 dernières pages
                  pageToShow = totalPages - 4 + i;
                } else {
                  // Sinon, afficher 2 pages avant et 2 pages après la page courante
                  pageToShow = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageToShow}
                    variant={currentPage === pageToShow ? "default" : "outline"}
                    size="sm"
                    onClick={() => paginate(pageToShow)}
                    className={currentPage === pageToShow 
                      ? "bg-red-600 hover:bg-red-700 text-white" 
                      : "border-white/10 text-white hover:bg-white/5"}
                  >
                    {pageToShow}
                  </Button>
                );
              })}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="border-white/10 text-white hover:bg-white/5"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Boîte de dialogue de confirmation */}
      {renderConfirmDialog()}
    
      {/* Boîte de dialogue pour afficher les avis */}
      <Dialog open={isReviewsDialogOpen} onOpenChange={setIsReviewsDialogOpen}>
      <DialogContent className="sm:max-w-[600px] bg-gray-900 text-white border-gray-800">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Star className="mr-2 text-yellow-400" size={18} />
              Avis sur le cours : {currentCourseTitle}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white"
              onClick={() => setIsReviewsDialogOpen(false)}
            >
              <X size={18} />
            </Button>
          </DialogTitle>
          <div className="flex space-x-2 mt-4">
            <Button
              variant={reviewFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReviewFilter('all')}
              className={reviewFilter === 'all' ? 'bg-red-600 hover:bg-red-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              Tous
            </Button>
            <Button
              variant={reviewFilter === 'published' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReviewFilter('published')}
              className={reviewFilter === 'published' ? 'bg-green-600 hover:bg-green-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              Publiés
            </Button>
            <Button
              variant={reviewFilter === 'pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReviewFilter('pending')}
              className={reviewFilter === 'pending' ? 'bg-yellow-600 hover:bg-yellow-700' : 'border-white/20 text-white hover:bg-white/10'}
            >
              En attente
            </Button>
          </div>
        </DialogHeader>
        
        {isLoadingReviews ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        ) : currentCourseReviews.length > 0 ? (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {currentCourseReviews
              .filter(review => {
                if (reviewFilter === 'all') return true;
                if (reviewFilter === 'published') return review.is_published;
                if (reviewFilter === 'pending') return !review.is_published;
                return true;
              })
              .map((review) => (
              <div key={review.id} className={`bg-gray-800 rounded-lg p-4 ${!review.is_published ? 'border-l-4 border-yellow-500' : ''}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <Avatar className="h-10 w-10 mr-3">
                      <AvatarImage src={review.student?.avatar_url} alt={review.student?.name} />
                      <AvatarFallback className="bg-gray-700 text-white">
                        {review.student?.name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{review.student?.name}</p>
                      <p className="text-sm text-white/60">
                        {format(new Date(review.created_at), 'dd MMMM yyyy', { locale: fr })}
                      </p>
                      <div className="mt-1">
                        {review.is_published ? (
                          <Badge className="bg-green-500/20 text-green-400">Publié</Badge>
                        ) : (
                          <Badge className="bg-yellow-500/20 text-yellow-400">En attente</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center bg-gray-700 px-2 py-1 rounded">
                    <Star size={14} className="text-yellow-400 mr-1" />
                    <span>{review.rating}</span>
                  </div>
                </div>
                <p className="text-white/80 mt-2">{review.review_text}</p>
                
                <div className="flex justify-end space-x-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleReviewStatus(review.id, review.is_published)}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    {review.is_published ? 'Dépublier' : 'Publier'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteReview(review.id)}
                    className="border-red-500/50 text-red-400 hover:bg-red-900/20"
                  >
                    Supprimer
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <MessageSquare size={40} className="mx-auto text-white/30 mb-3" />
            <p>Aucun avis disponible pour ce cours.</p>
            <p className="text-sm text-white/50 mt-2">Les avis apparaîtront ici lorsque des étudiants en auront soumis.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  </AdminLayout>
  );
};

export default AdminCoursesPage;
